
import React, { useState, useEffect } from 'react';
import { X, MessageCircle, AlertTriangle, CreditCard, ShieldAlert, ChevronRight, ArrowLeft, Send, History, RefreshCw } from 'lucide-react';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../App';
import { CHATS_COLLECTION } from '../../config/constants';

const SupportOption = ({ icon: Icon, title, onClick, color, description }) => (
    <button onClick={onClick} className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-blue-50 rounded-2xl border border-transparent hover:border-blue-100 transition-all active:scale-[0.98] group text-left">
        <div className="flex items-center min-w-0">
            <div className={`p-2.5 rounded-xl ${color} mr-4 shadow-sm group-hover:scale-110 transition-transform flex-shrink-0`}>
                <Icon size={20} />
            </div>
            <div className="min-w-0">
                <span className="font-bold text-gray-700 text-sm block leading-none">{title}</span>
                {description && <span className="text-[10px] text-gray-400 font-medium truncate block mt-1">{description}</span>}
            </div>
        </div>
        <ChevronRight size={16} className="text-gray-300 ml-2" />
    </button>
);

const SupportSelectionModal = ({ onClose, onSelectSubject }) => {
    const { db, user } = useAppContext();
    const navigate = useNavigate(); // CORREÇÃO: Inicialização correta do hook de navegação
    const [step, setStep] = useState(1);
    const [selectedSubject, setSelectedSubject] = useState('');
    const [description, setDescription] = useState('');
    const [history, setHistory] = useState([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);

    useEffect(() => {
        if (step === 3 && user && db) {
            const fetchHistory = async () => {
                setIsLoadingHistory(true);
                try {
                    const supportId = "SYSTEM_ADMIN_SUPPORT";
                    const q = query(
                        collection(db, CHATS_COLLECTION),
                        where("participants", "array-contains", user.uid),
                        where("serviId", "==", supportId),
                        orderBy("lastMessageTimestamp", "desc")
                    );
                    const snap = await getDocs(q);
                    setHistory(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                } catch (e) { console.error("Erro ao carregar histórico:", e); }
                finally { setIsLoadingHistory(false); }
            };
            fetchHistory();
        }
    }, [step, user, db]);

    const handleSubjectClick = (subject) => {
        setSelectedSubject(subject);
        setStep(2);
    };

    const handleFinalSubmit = () => {
        if (description.trim().length < 10) return;
        onSelectSubject(selectedSubject, description);
    };

    const handleOpenChat = (chatId) => {
        onClose();
        navigate(`/chat/${chatId}`);
    };

    return (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-end justify-center sm:items-center p-4">
            <div className="bg-white w-full max-w-sm rounded-[2.5rem] shadow-2xl overflow-hidden animate-slide-up">
                <div className="p-6 border-b border-gray-50 flex justify-between items-center">
                    <div className="flex items-center">
                        {step > 1 && (
                            <button onClick={() => setStep(1)} className="mr-3 p-1 hover:bg-gray-100 rounded-lg transition-colors text-gray-400">
                                <ArrowLeft size={20} />
                            </button>
                        )}
                        <h3 className="text-lg font-black text-gray-800 uppercase tracking-tighter">
                            {step === 1 ? "Suporte" : step === 2 ? "Conte-nos mais" : "Meus Chamados"}
                        </h3>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors"><X size={20} className="text-gray-400" /></button>
                </div>

                <div className="p-6 overflow-y-auto max-h-[60vh]">
                    {step === 1 ? (
                        <div className="space-y-3">
                            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4">Escolha uma opção:</p>
                            <div className="grid gap-2 text-left">
                                <SupportOption icon={History} title="Ver Meus Chamados" color="bg-indigo-100 text-indigo-600" description="Histórico de atendimentos" onClick={() => setStep(3)} />
                                <div className="h-4"></div>
                                <SupportOption icon={CreditCard} title="Problema com Pagamento" color="bg-emerald-100 text-emerald-600" onClick={() => handleSubjectClick("Pagamento")} />
                                <SupportOption icon={ShieldAlert} title="Denunciar Conduta" color="bg-red-100 text-red-600" onClick={() => handleSubjectClick("Denúncia")} />
                                <SupportOption icon={AlertTriangle} title="Erro no Aplicativo" color="bg-amber-100 text-amber-600" onClick={() => handleSubjectClick("Erro no App")} />
                                <SupportOption icon={MessageCircle} title="Dúvidas ou Sugestões" color="bg-blue-100 text-blue-600" onClick={() => handleSubjectClick("Dúvida")} />
                            </div>
                        </div>
                    ) : step === 2 ? (
                        <div className="space-y-4 animate-fade-in text-left">
                            <div className="bg-blue-50 p-3 rounded-xl border border-blue-100">
                                <p className="text-[10px] font-black text-blue-600 uppercase">Assunto:</p>
                                <p className="text-sm font-bold text-blue-800">{selectedSubject}</p>
                            </div>
                            <div>
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 block">Descreva sua necessidade:</label>
                                <textarea
                                    autoFocus
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Digite aqui."
                                    className="w-full p-4 bg-gray-50 border-2 border-transparent focus:border-blue-500 rounded-2xl h-32 outline-none text-sm font-medium transition-all"
                                />
                                <p className={`text-[9px] mt-2 font-bold ${description.length < 10 ? 'text-orange-400' : 'text-emerald-500'}`}>
                                    Mínimo de 10 caracteres ({description.length})
                                </p>
                            </div>
                            <button
                                onClick={handleFinalSubmit}
                                disabled={description.trim().length < 10}
                                className="w-full py-4 bg-blue-600 disabled:bg-gray-200 text-white font-black rounded-2xl shadow-lg active:scale-95 transition-all flex items-center justify-center uppercase text-xs tracking-widest"
                            >
                                <Send size={16} className="mr-2" /> Iniciar Atendimento
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-3 animate-fade-in text-left">
                            {isLoadingHistory ? (
                                <div className="py-10 text-center"><RefreshCw className="animate-spin mx-auto text-blue-600" /></div>
                            ) : history.length > 0 ? (
                                history.map(chat => (
                                    <button key={chat.id} onClick={() => handleOpenChat(chat.id)} className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-transparent hover:border-blue-100 transition-all text-left">
                                        <div className="min-w-0">
                                            <p className="font-bold text-gray-800 text-xs truncate">{chat.serviceTitle}</p>
                                            <p className="text-[10px] text-gray-400 truncate mt-1 italic">"{chat.lastMessage}"</p>
                                        </div>
                                        <ChevronRight size={14} className="text-gray-300 flex-shrink-0" />
                                    </button>
                                ))
                            ) : (
                                <div className="py-10 text-center opacity-40">
                                    <MessageCircle size={40} className="mx-auto mb-2" />
                                    <p className="text-xs font-black uppercase">Nenhum chamado</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="p-6 bg-gray-50/50">
                    <p className="text-[9px] text-gray-400 text-center font-medium">
                        {step === 3 ? "Clique em um chamado para continuar a conversa." : "Chamados sem descrição clara serão encerrados automaticamente."}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default SupportSelectionModal;
